import { Copy, Loader2, Share2 } from 'lucide-react';
import { Button } from '@tark/shared/components';

export function CompetitionActionPanel({
  mobile = false,
  selectedName,
  selectedStatus,
  actionLabel,
  actionDisabled,
  isChecking,
  partnerInviteReady,
  shareStatus,
  error,
  onAction,
  onShare,
}: {
  mobile?: boolean;
  selectedName: string;
  selectedStatus?: string;
  actionLabel: string;
  actionDisabled: boolean;
  isChecking: boolean;
  partnerInviteReady: boolean;
  shareStatus?: string | null;
  error?: string | null;
  onAction: () => void;
  onShare: () => void;
}) {
  if (mobile) {
    return (
      <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] left-3 right-3 z-30 flex items-center gap-3 rounded-lg border border-border bg-surface p-3 shadow-2xl lg:hidden">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-text">
            {partnerInviteReady ? 'Partner link ready' : selectedName}
          </p>
          <p className={`truncate text-xs ${error ? 'text-red-600' : 'text-text-muted'}`}>
            {error || (partnerInviteReady ? 'Send it to your intended partner' : selectedStatus)}
          </p>
        </div>
        <Button
          size="sm"
          onClick={partnerInviteReady ? onShare : onAction}
          disabled={partnerInviteReady ? false : actionDisabled}
          leftIcon={partnerInviteReady ? <Share2 /> : undefined}
        >
          {partnerInviteReady ? 'Share link' : actionLabel}
        </Button>
      </div>
    );
  }

  return (
    <aside className="hidden rounded-lg border border-border bg-surface p-5 lg:sticky lg:top-5 lg:block">
      <p className="text-xs font-semibold uppercase text-text-subtle">Selected division</p>
      <h2 className="mt-2 text-lg font-bold text-text">{selectedName}</h2>
      {selectedStatus ? <p className="mt-1 text-sm text-text-muted">{selectedStatus}</p> : null}
      {error ? (
        <div role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      ) : null}

      {isChecking ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking your entry
        </p>
      ) : null}

      <Button className="mt-5" fullWidth onClick={onAction} disabled={actionDisabled}>
        {actionLabel}
      </Button>

      {partnerInviteReady ? (
        <div className="mt-5 border-t border-border pt-5">
          <p className="font-semibold text-text">Partner link ready</p>
          <p className="mt-1 text-sm text-text-muted">Send this private link only to your intended partner.</p>
          <div className="mt-3 flex gap-2">
            <Button fullWidth onClick={onShare} leftIcon={<Share2 />}>
              Share link
            </Button>
            <Button variant="outline" aria-label="Copy partner link" onClick={onShare} leftIcon={<Copy />}>
              Copy
            </Button>
          </div>
          {shareStatus ? <p className="mt-2 text-xs font-semibold text-primary">{shareStatus}</p> : null}
        </div>
      ) : null}
    </aside>
  );
}
